<template>
    <v-container class="pa-5" fluid>
        <AssignComponentDialog
            :is-open="assignComponentDialog"
            :product-components="productComponents"
            @on-save="addProductComponent"
            @on-close="closeAssignComponentDialog"
        />

        <AssignProductionPropertiesDialog
            :is-open="assignProductionPropertiesDialog"
            :component="currentProductionComponent"
            :properties="productProductionProperties"
            @on-save="saveProductProductionProperties"
            @on-close="closeAssignProductionPropertiesDialog"
        />

        <AssignProductionProcessDialog
            :is-open="assignProductionProcessDialog"
            :component="currentProductionComponent"
            :processes="productionProcesses"
            @on-save="saveProductionProcess"
            @on-close="closeProductionProcessDialog"
        />

        <ConfirmDialog
            :is-open="confirmRemoveComponentDialog"
            :data="currentProductionComponentIndex"
            @on-confirm="removeProductComponent(currentProductionComponentIndex)"
            @on-abort="closeConfirmRemoveComponentDialog"
        >
            <template #title>
                Usunąć komponent z produktu?
            </template>
            <template #content>
                Czy na pewno chcesz usunąć komponent?
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmLeaveDialog"
            @on-confirm="acceptConfirmLeaveDialog"
            @on-abort="closeConfirmLeaveDialog"
        >
            <template #title>
                Opuścić stronę?
            </template>
            <template #content>
                Szablon produktu nie został zapisany. Czy chcesz opuścić stronę?
            </template>
        </ConfirmDialog>

        <page-title>{{ pageTitle }}</page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Parametry szablonu
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row>
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="productTemplate.name"
                                                label="Nazwa szablonu"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateNameErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-autocomplete
                                                v-model="productTemplate.jsonTemplate.productTypeId"
                                                :items="products"
                                                label="Rodzaj produktu"
                                                item-text="name"
                                                item-value="id"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateTypeErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="productTemplate.jsonTemplate.formatA"
                                                label="Szerokość"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :error-messages="productTemplateFormatAErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="productTemplate.jsonTemplate.formatB"
                                                label="Wysokość"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :error-messages="productTemplateFormatBErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                v-model="productTemplate.jsonTemplate.description"
                                                label="Domyślne uwagi"
                                                outlined
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="productTemplate.tag"
                                                label="Tag"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateTagErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-select
                                                v-model="productTemplate.jsonTemplate.productionFileInPathId"
                                                :items="productionFileInPaths"
                                                item-text="name"
                                                item-value="id"
                                                label="Ścieżka pliku produkcyjnego"
                                                outlined
                                                dense
                                                clearable
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Komponenty
                            </h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="secondary"
                                fab
                                small
                                data-sentry-label="Dodaj komponent"
                                class="elevation-1"
                                @click="showAssignComponentDialog"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col v-if="productTemplate.jsonTemplate.orderProductComponents.length === 0" cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Produkt nie posiada przypisanych komponentów.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <template v-else>
                            <v-col v-for="(component, componentIndex) in productTemplate.jsonTemplate.orderProductComponents" :key="component.id" cols="12">
                                <v-card>
                                    <v-card-title>
                                        <v-row align="center">
                                            <v-col class="text-no-wrap">
                                                <div class="text-h6">
                                                    {{ component.component.name }} ({{ component.component.code }})
                                                </div>
                                            </v-col>
                                            <v-col sm="auto">
                                                <v-btn
                                                    color="primary"
                                                    medium
                                                    text
                                                    data-sentry-label="Dodaj cechy produkcyjne"
                                                    class="mx-1"
                                                    @click="showAssignProductionPropertiesDialog(component, componentIndex)"
                                                >
                                                    Dodaj cechy produkcyjne
                                                </v-btn>
                                                <v-btn
                                                    color="primary"
                                                    medium
                                                    text
                                                    data-sentry-label="Dodaj procesy produkcyjne"
                                                    class="mx-1"
                                                    @click="showAssignProductionProcessDialog(component, componentIndex)"
                                                >
                                                    Dodaj procesy produkcyjne
                                                </v-btn>
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Skopiuj komponent"
                                                            v-on="on"
                                                            @click="copyComponent(component, componentIndex)"
                                                        >
                                                            <v-icon color="primary">
                                                                mdi-content-copy
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Skopiuj komponent</span>
                                                </v-tooltip>
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń komponent"
                                                            v-on="on"
                                                            @click="showConfirmRemoveComponentDialog(componentIndex)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń komponent</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                    </v-card-title>

                                    <v-divider />

                                    <v-card-text>
                                        <v-alert
                                            v-if="component.orderComponentProperties.length === 0"
                                            type="info"
                                            text
                                            border="left"
                                        >
                                            Brak przypisanych cech produkcyjnych.
                                        </v-alert>
                                        <v-row v-else>
                                            <v-col cols="12">
                                                <strong>Cechy produkcyjne:</strong>
                                            </v-col>
                                            <v-col
                                                v-for="(productionProperty, propertyIndex) in component.orderComponentProperties"
                                                :key="productionProperty.productProductionProperty.id"
                                                cols="12"
                                                lg="3"
                                            >
                                                <div class="d-inline-flex">
                                                    <v-autocomplete
                                                        v-if="productionProperty.productProductionProperty.name === 'rodzaj_papieru'"
                                                        v-model="productionProperty.value"
                                                        :items="papers"
                                                        :filter="customFilter"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        item-value="name"
                                                        item-text="name"
                                                        no-data-text="Nie znaleziono"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                    />
                                                    <v-autocomplete
                                                        v-else-if="productionProperty.productProductionProperty.name === 'rodzaj_skladania'"
                                                        v-model="productionProperty.value"
                                                        :items="foldingTypes"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        item-value="name"
                                                        item-text="name"
                                                        no-data-text="Nie znaleziono"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                        @change="handleFoldingTypeChange(productionProperty)"
                                                    />
                                                    <v-text-field
                                                        v-else
                                                        v-model="productionProperty.value"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                    />
                                                    <v-btn
                                                        data-sentry-label="Usuń cechę produkcyjną"
                                                        icon
                                                        class="mt-1"
                                                        @click="removeProductProductionProperty(componentIndex, productionProperty)"
                                                    >
                                                        <v-icon>
                                                            mdi-close
                                                        </v-icon>
                                                    </v-btn>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>

                                    <v-divider />

                                    <v-card-text>
                                        <v-alert
                                            v-if="component.orderComponentProcesses.length === 0"
                                            type="info"
                                            text
                                            border="left"
                                        >
                                            Brak przypisanych procesów produkcyjnych.
                                        </v-alert>
                                        <v-row v-else>
                                            <v-col cols="12">
                                                <strong>Procesy produkcyjne:</strong>
                                            </v-col>

                                            <v-col cols="12">
                                                <v-chip
                                                    v-for="(productionProcess) in component.orderComponentProcesses"
                                                    :key="productionProcess.productionProcess.id"
                                                    close
                                                    class="ma-1"
                                                    @update:active="removeProductionProcess(componentIndex, productionProcess)"
                                                >
                                                    {{ productionProcess.productionProcess.name }}
                                                </v-chip>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </template>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-btn color="success" medium :loading="actionLoading" @click="saveProductTemplate">
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import AssignComponentDialog from './../../Configurations/Common/AssignComponentDialog'
import AssignProductionPropertiesDialog from './../../Configurations/Common/AssignProductionPropertiesDialog'
import AssignProductionProcessDialog from './../../Configurations/Common/AssignProductionProcessDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import tooltipDelay from '@/mixins/tooltip'
import customFilters from '@/mixins/customFilters'
import { errorHandler } from '@/utils/errorHandler'
import { required, integer, maxLength } from 'vuelidate/lib/validators'

export default {
    name: 'ProductTemplate',
    components: {
        PageTitle,
        AssignComponentDialog,
        AssignProductionPropertiesDialog,
        AssignProductionProcessDialog,
        ConfirmDialog
    },
    mixins: [
        tooltipDelay,
        customFilters
    ],
    beforeRouteEnter(to, from, next) {
        //Edycja szablonu produktu
        if (typeof to.params.id !== 'undefined') {
            store.dispatch('orders/getProductTemplate', to.params.id)
                .then(response => {
                    Promise.all([
                        store.dispatch('configurations/getProducts'),
                        store.dispatch('configurations/getProductProductionProperties', {
                            query: '?orderAvailable=true'
                        }),
                        store.dispatch('configurations/getProductComponents'),
                        store.dispatch('configurations/getProductionProcesses', { paginate: '', query: '?isVirtual=false' }),
                        store.dispatch('configurations/getPapers'),
                        store.dispatch('orders/getProductionFileInPaths'),
                        store.dispatch('configurations/getFoldingTypes'),
                    ])
                        .then(responses => {
                            next(vm => {
                                vm.products = responses[0].data.results
                                vm.productProductionProperties = responses[1].data.results
                                vm.productComponents = responses[2].data.results
                                vm.productionProcesses = responses[3].data.results
                                vm.papers = responses[4].data.results
                                vm.productionFileInPaths = responses[5].data.results
                                vm.foldingTypes = responses[6].data.results
                                vm.actionName = 'updateProductTemplate'
                                vm.productTemplate = Object.assign({}, response.data)
                                vm.pageTitle = `Szablony produktowe / Edycja szablonu produktu / ${vm.productTemplate.name}`
                            })
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    errorHandler(error)
                })
        } else if (typeof to.query.copyId !== 'undefined') {
            store.dispatch('orders/getProductTemplate', to.query.copyId)
                .then(response => {
                    Promise.all([
                        store.dispatch('configurations/getProducts', {
                            shouldSavedInStore: false
                        }),
                        store.dispatch('configurations/getProductProductionProperties', {
                            query: '?orderAvailable=true'
                        }),
                        store.dispatch('configurations/getProductComponents'),
                        store.dispatch('configurations/getProductionProcesses', { paginate: '', query: '?isVirtual=false' }),
                        store.dispatch('configurations/getPapers'),
                        store.dispatch('orders/getProductionFileInPaths'),
                        store.dispatch('configurations/getFoldingTypes'),
                    ])
                        .then(responses => {
                            next(vm => {
                                const copiedProductTemplate = JSON.parse(JSON.stringify(response.data))

                                copiedProductTemplate.name = null
                                delete copiedProductTemplate.id

                                vm.products = responses[0].data.results
                                vm.productProductionProperties = responses[1].data.results
                                vm.productComponents = responses[2].data.results
                                vm.productionProcesses = responses[3].data.results
                                vm.papers = responses[4].data.results
                                vm.productionFileInPaths = responses[5].data.results
                                vm.foldingTypes = responses[6].data.results
                                vm.actionName = 'createProductTemplate'
                                vm.productTemplate = copiedProductTemplate
                                vm.pageTitle = to.meta.title
                                vm.hasProductChanges = true
                            })
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    errorHandler(error)
                })
            // Tworzenie szablonu produktu
        } else {
            Promise.all([
                store.dispatch('configurations/getProducts', {
                    shouldSavedInStore: false
                }),
                store.dispatch('configurations/getProductProductionProperties', {
                    query: '?orderAvailable=true'
                }),
                store.dispatch('configurations/getProductComponents'),
                store.dispatch('configurations/getProductionProcesses', { paginate: '', query: '?isVirtual=false' }),
                store.dispatch('configurations/getPapers'),
                store.dispatch('orders/getProductionFileInPaths')
            ])
                .then(responses => {
                    next(vm => {
                        vm.products = responses[0].data.results
                        vm.productProductionProperties = responses[1].data.results
                        vm.productComponents = responses[2].data.results,
                        vm.productionProcesses = responses[3].data.results,
                        vm.papers = responses[4].data.results
                        vm.productionFileInPaths = responses[5].data.results
                        vm.actionName = 'createProductTemplate'
                        vm.pageTitle = to.meta.title
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasProductChanges) {
            this.confirmLeaveDialog = true
            this.confirmLeaveDialogNextRouteName = to.name
        } else {
            next()
        }
    },
    data() {
        return {
            papers: [],
            actionName: '',
            pageTitle: '',
            productTemplate: {
                name: null,
                tag: null,
                jsonTemplate: {
                    productionFileInPathId: null,
                    productTypeId: null,
                    formatA: null,
                    formatB: null,
                    orderProductComponents: [],
                    description: null
                }
            },
            currentProductionComponent: {
                componentId: null,
                component: {
                    name: null,
                    code: null
                },
                orderComponentProperties: [],
                orderComponentProcesses: []
            },
            currentProductionComponentIndex: null,
            products: [],
            productComponents: [],
            productProductionProperties: [],
            productionProcesses: [],
            productionFileInPaths: [],
            actionLoading: false,
            assignComponentDialog: false,
            confirmRemoveComponentDialog: false,
            assignProductionPropertiesDialog: false,
            hasProductChanges: false,
            confirmLeaveDialog: false,
            confirmLeaveDialogNextRouteName: null,
            assignProductionProcessDialog: false,
            foldingTypes: [],
        }
    },
    validations() {
        const rules = {
            productTemplate: {
                name: {
                    required
                },
                jsonTemplate: {
                    productTypeId: {
                        required
                    },
                    orderProductComponents: {
                        $each: {
                            orderComponentProperties: {
                                $each: {
                                    value: {
                                        required
                                    }
                                }
                            }
                        }
                    }
                },
                tag: {
                    maxLength: maxLength(24)
                }
            },
        }

        if (this.productTemplate.jsonTemplate.formatA) {
            rules.productTemplate.jsonTemplate['formatA'] = {
                integer
            }
        }

        if (this.productTemplate.jsonTemplate.formatB) {
            rules.productTemplate.jsonTemplate['formatB'] = {
                integer
            }
        }

        return rules
    },
    methods: {
        handleFoldingTypeChange(productionProperty) {
            const componentIndex = this.productTemplate.jsonTemplate.orderProductComponents.findIndex(
                comp => comp.orderComponentProperties.includes(productionProperty)
            )

            if (componentIndex !== -1) {
                const component = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex]
                const foldingType = this.foldingTypes.find(type => type.name === productionProperty.value)

                if (foldingType && foldingType.numberOfFoldingsRequired) {
                    const foldingNumberProperty = component.orderComponentProperties?.find(
                        prop => prop.productProductionProperty.name === 'ilosc_falcow'
                    )

                    if (!foldingNumberProperty) {
                        const foldingNumberPropertyDef = this.productProductionProperties.find(
                            prop => prop.name === 'ilosc_falcow'
                        )

                        if (foldingNumberPropertyDef) {
                            component.orderComponentProperties.push({
                                productProductionPropertyId: foldingNumberPropertyDef.id,
                                productProductionProperty: foldingNumberPropertyDef,
                                value: ''
                            })
                        }
                    }
                } else {
                    const foldingNumberPropertyIndex = component.orderComponentProperties.findIndex(
                        property => property.productProductionProperty.name === 'ilosc_falcow'
                    )

                    if (foldingNumberPropertyIndex !== -1) {
                        component.orderComponentProperties.splice(foldingNumberPropertyIndex, 1)
                    }
                }
            }

            this.hasProductChanges = true
        },
        showAssignComponentDialog() {
            this.assignComponentDialog = true
        },
        closeAssignComponentDialog() {
            this.assignComponentDialog = false
        },
        addProductComponent(component) {
            this.hasProductChanges = true
            this.productTemplate.jsonTemplate.orderProductComponents.push({
                component: component,
                componentId: component.id,
                orderComponentProcesses: [],
                orderComponentProperties: []
            })
        },
        removeProductComponent(componentIndex) {
            this.hasProductChanges = true
            this.productTemplate.jsonTemplate.orderProductComponents.splice(componentIndex, 1)
            this.closeConfirmRemoveComponentDialog()
        },
        showConfirmRemoveComponentDialog(componentIndex) {
            this.confirmRemoveComponentDialog = true
            this.currentProductionComponentIndex = componentIndex
        },
        closeConfirmRemoveComponentDialog() {
            this.confirmRemoveComponentDialog = false
            this.clearCurrentProductionComponent()
        },
        showAssignProductionPropertiesDialog(component, index) {
            this.assignProductionPropertiesDialog = true
            this.currentProductionComponent = component
            this.currentProductionComponentIndex = index
        },
        closeAssignProductionPropertiesDialog() {
            this.assignProductionPropertiesDialog = false
            setTimeout(() => {
                this.clearCurrentProductionComponent()
            }, 500)
        },
        saveProductProductionProperties(productionProperties) {
            productionProperties.forEach(item => {
                this.productTemplate.jsonTemplate.orderProductComponents[this.currentProductionComponentIndex].orderComponentProperties.push({
                    productProductionPropertyId: item.id,
                    productProductionProperty: item
                })
            })

            this.hasProductChanges = true
        },
        removeProductProductionProperty(componentIndex, productionProperty) {
            const orderComponentProperties = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex].orderComponentProperties

            orderComponentProperties.splice(orderComponentProperties.indexOf(productionProperty), 1)

            this.hasProductChanges = true
        },
        showAssignProductionProcessDialog(component, index) {
            this.assignProductionProcessDialog = true
            this.currentProductionComponent = component
            this.currentProductionComponentIndex = index
        },
        saveProductionProcess(productionProcesses) {
            productionProcesses.forEach(item => {
                this.productTemplate.jsonTemplate.orderProductComponents[this.currentProductionComponentIndex].orderComponentProcesses.push({
                    order: this.currentProductionComponent.orderComponentProcesses.length + 1,
                    productionProcessId: item.id,
                    productionProcess: item
                })
            })

            this.hasProductChanges = true
        },
        removeProductionProcess(componentIndex, productionProcess) {
            const orderComponentProcesses = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex].orderComponentProcesses

            orderComponentProcesses.splice(orderComponentProcesses.indexOf(productionProcess), 1)


            this.hasProductChanges = true
        },
        clearCurrentProductionComponent() {
            this.currentProductionComponent = {
                componentId: null,
                component: {
                    name: null,
                    code: null
                },
                orderComponentProperties: [],
                orderComponentProcesses: []
            }
            this.currentProductionComponentIndex = null
        },
        copyComponent(component) {
            this.productTemplate.jsonTemplate.orderProductComponents.push(JSON.parse(JSON.stringify(component)))
        },
        closeProductionProcessDialog() {
            this.assignProductionProcessDialog = false
            setTimeout(() => {
                this.clearCurrentProductionComponent()
            }, 500)
        },
        saveProductTemplate() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                if (this.actionName == 'createProductTemplate') {
                    const payload = {
                        name: this.productTemplate.name,
                        jsonTemplate: JSON.stringify(this.productTemplate.jsonTemplate),
                        tag: this.productTemplate.tag
                    }

                    this.actionLoading = true
                    this.$store.dispatch(`orders/${this.actionName}`, payload)
                        .then(() => {
                            this.$v.$reset()
                            this.hasProductChanges = false
                            this.close()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.actionLoading = false
                        })
                }
                if (this.actionName == 'updateProductTemplate') {
                    const payload = {
                        id: this.productTemplate.id,
                        name: this.productTemplate.name,
                        jsonTemplate: JSON.stringify(this.productTemplate.jsonTemplate),
                        tag: this.productTemplate.tag
                    }

                    this.actionLoading = true
                    this.$store.dispatch(`orders/${this.actionName}`, payload)
                        .then(() => {
                            this.$v.$reset()
                            this.hasProductChanges = false
                            this.close()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.actionLoading = false
                        })
                }
            }
        },
        closeConfirmLeaveDialog() {
            this.confirmLeaveDialog = false
        },
        acceptConfirmLeaveDialog() {
            this.hasProductChanges = false
            this.$router.push({ name: this.confirmLeaveDialogNextRouteName })
                .catch(error => {
                    errorHandler(error)
                })
        },
        close() {
            this.$router.push({ name: 'ordersProductTemplates' })
                .catch(error => {
                    errorHandler(error)
                })
        },

        // Walidacje
        productTemplateNameErrors() {
            const errors = []

            if (!this.$v.productTemplate.name.$dirty) return errors
            !this.$v.productTemplate.name.required && errors.push('Proszę podać nazwę produktu.')

            return errors
        },
        productTemplateTypeErrors() {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.productTypeId.$dirty) return errors
            !this.$v.productTemplate.jsonTemplate.productTypeId.required && errors.push('Proszę podać rodzaj produktu.')

            return errors
        },
        productTemplateTagErrors() {
            const errors = []

            if (!this.$v.productTemplate.tag.$dirty) return errors
            !this.$v.productTemplate.tag.maxLength && errors.push('Tag nie może przekroczyć 24 znaków.')

            return errors
        },
        productTemplateFormatAErrors() {
            if (this.productTemplate.jsonTemplate.formatA) {
                const errors = []

                if (!this.$v.productTemplate.jsonTemplate.formatA.$dirty) return errors
                this.$v.productTemplate.jsonTemplate.formatA.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productTemplateFormatBErrors() {
            if (this.productTemplate.jsonTemplate.formatB) {
                const errors = []

                if (!this.$v.productTemplate.jsonTemplate.formatB.$dirty) return errors
                this.$v.productTemplate.jsonTemplate.formatB.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productionPropertyValueErrors(componentIndex, propertyIndex) {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.orderProductComponents.$each[componentIndex].orderComponentProperties.$each[propertyIndex].$dirty) return errors

            !this.$v.productTemplate.jsonTemplate.orderProductComponents.$each[componentIndex].orderComponentProperties.$each[propertyIndex].value.required && errors.push('Proszę podać wartość.')

            return errors
        }
    }
}
</script>
